<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import CustomTable from "@/components/tables/custom-table.vue";
import SyncLogDetailModal from "./sync-log-detail-modal.vue";
import Status from "@/helpers/general/status";

/**
 * Sync Logs Component - Zoho Integration
 */
export default {
  components: {
    Layout,
    PageHeader,
    CustomTable,
    SyncLogDetailModal
  },
  data() {
    return {
      detailModalId: 'sync-log-detail-modal',
      pageHeader: {
        title: "Logs de Sincronización Zoho",
        breadcrumbs: [
          {
            text: "Integraciones",
            href: "/integrations",
          },
          {
            text: "Logs de Sincronización",
            href: "/integrations/zoho/sync-logs",
            active: true
          }
        ],
      },
      table: {
        url: '/sync-logs',
        params: {},
        fields: [
          { key: "id", label: "ID", sortable: true },
          { key: "entity_info", label: "Entidad", sortable: false },
          { key: "direction", label: "Dirección", sortable: true },
          { key: "operation", label: "Operación", sortable: true },
          { key: "status", label: "Estado", sortable: true },
          { key: "zoho_module", label: "Módulo Zoho", sortable: true },
          { key: "zoho_id", label: "Zoho ID", sortable: false },
          { key: "attempts_info", label: "Intentos", sortable: false },
          { key: "created_at", label: "Fecha Creación", sortable: true },
          { key: "processed_at", label: "Fecha Procesamiento", sortable: true },
        ],
        formatted_fields: [
          "direction",
          "operation",
          "status",
          "entity_info",
          "attempts_info",
          "created_at",
          "processed_at"
        ]
      },
      selectedItemId: null,
    };
  },
  computed: {
    syncStatuses() {
      return Status.getList('sync_log_status');
    },
    syncDirections() {
      return Status.getList('sync_log_direction');
    },
    syncOperations() {
      return Status.getList('sync_log_operation');
    },
    entityTypes() {
      return [
        { id: 'App\\Models\\Customer', label: 'Cliente' },
        { id: 'App\\Models\\Collaborator', label: 'Colaborador' },
        { id: 'App\\Models\\Product', label: 'Producto' }
      ];
    }
  },
  methods: {
    showDetailModal(item) {
      this.selectedItemId = item?.id || null;
      this.$bvModal.show(this.detailModalId);
    },
    formatter(key, item) {
      switch (key) {
        case 'direction':
          return this.formatDirection(item[key]);
        case 'operation':
          return this.formatOperation(item[key]);
        case 'status':
          return this.formatStatus(item[key]);
        case 'entity_info':
          return this.formatEntityInfo(item);
        case 'attempts_info':
          return this.formatAttempts(item);
        case 'created_at':
        case 'processed_at':
          return this.formatDate(item[key]);
        default:
          return item[key];
      }
    },
    formatDirection(direction) {
      return Status.getStatus({ itemStatus: direction, type: 'sync_log_direction', html: true });
    },
    formatOperation(operation) {
      return Status.getStatus({ itemStatus: operation, type: 'sync_log_operation', html: true });
    },
    formatStatus(status) {
      return Status.getStatus({ itemStatus: status, type: 'sync_log_status', html: true });
    },
    formatEntityInfo(item) {
      const entityType = item?.entity_type?.split('\\').pop() || 'N/A';
      return `<span class="text-muted">${entityType}</span> #${item?.entity_id || 'N/A'}`;
    },
    formatAttempts(item) {
      const current = item?.attempts || 0;
      const max = item?.max_attempts || 0;
      const percentage = max > 0 ? (current / max) * 100 : 0;
      let badgeClass = 'badge-soft-success';
      
      if (percentage >= 75) {
        badgeClass = 'badge-soft-danger';
      } else if (percentage >= 50) {
        badgeClass = 'badge-soft-warning';
      }
      
      return `<span class="badge ${badgeClass}">${current}/${max}</span>`;
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    }
  }
};
</script>

<template>
  <Layout>
    <PageHeader :title="pageHeader?.title" :items="pageHeader?.breadcrumbs" />
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <SyncLogDetailModal 
              :modal-id="detailModalId" 
              :id="selectedItemId"
            />
            <CustomTable
              :url="table.url"
              :fields="table.fields"
              :params="table.params"
              :formatted-fields="table.formatted_fields"
              :formatter="formatter"
              ref="syncLogTable"
              :show-view="true"
              :show-edit="false"
              :showSoftDelete="false"
              @show="showDetailModal"
              :show-modal-id="detailModalId"
            >
              <template v-slot:filters="{ filters }">
                <div class="row">
                  <div class="form-group col-12 col-lg-3 col-sm-6">
                    <label for="sync_id">Sync ID</label>
                    <input 
                      id="sync_id" 
                      v-model="filters.sync_id" 
                      type="text" 
                      class="form-control" 
                      placeholder="Buscar por Sync ID"
                    />
                  </div>
                  <div class="form-group col-12 col-lg-3 col-sm-6">
                    <label for="status">Estado</label>
                    <select v-model="filters.status" class="form-control">
                      <option value="">Todos</option>
                      <option 
                        v-for="option in syncStatuses" 
                        :value="option.id" 
                        :key="option.id"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group col-12 col-lg-3 col-sm-6">
                    <label for="direction">Dirección</label>
                    <select v-model="filters.direction" class="form-control">
                      <option value="">Todas</option>
                      <option 
                        v-for="option in syncDirections" 
                        :value="option.id" 
                        :key="option.id"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group col-12 col-lg-3 col-sm-6">
                    <label for="operation">Operación</label>
                    <select v-model="filters.operation" class="form-control">
                      <option value="">Todas</option>
                      <option 
                        v-for="option in syncOperations" 
                        :value="option.id" 
                        :key="option.id"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group col-12 col-lg-3 col-sm-6">
                    <label for="entity_type">Tipo de Entidad</label>
                    <select v-model="filters.entity_type" class="form-control">
                      <option value="">Todos</option>
                      <option 
                        v-for="option in entityTypes" 
                        :value="option.id" 
                        :key="option.id"
                      >
                        {{ option.label }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group col-12 col-lg-3 col-sm-6">
                    <label for="zoho_module">Módulo Zoho</label>
                    <input 
                      id="zoho_module" 
                      v-model="filters.zoho_module" 
                      type="text" 
                      class="form-control" 
                      placeholder="Ej: Contacts, Accounts"
                    />
                  </div>
                  <div class="form-group col-12 col-lg-3 col-sm-6">
                    <label for="zoho_id">Zoho ID</label>
                    <input 
                      id="zoho_id" 
                      v-model="filters.zoho_id" 
                      type="text" 
                      class="form-control" 
                      placeholder="ID en Zoho"
                    />
                  </div>
                  <div class="form-group col-12 col-lg-3 col-sm-6">
                    <label for="is_from_webhook">Origen Webhook</label>
                    <select v-model="filters.is_from_webhook" class="form-control">
                      <option value="">Todos</option>
                      <option value="1">Sí</option>
                      <option value="0">No</option>
                    </select>
                  </div>
                </div>
              </template>
            </CustomTable>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
